import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { getIcon, formatPlatformName } from "../../constants/constants";
import { Icons } from "../../constants/icon-library";
import { getIntegration } from "../../features/meeting-integration/meetingIntegrationSlice";
import { upsertIntegrationSetting } from "../../features/integration-setting/integrationSettingSlice";
import { getParticipants } from "../../features/participant/participantSlice";
import { getApiKeySteps, getSetupConfig } from "./Integrations.utils";

const cardStyle = {
  background: "rgba(255,255,255,0.05)",
  backdropFilter: "blur(10px)",
  borderRadius: "14px",
};

function SetUp() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [visibleFields, setVisibleFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector((state) => state.user);
  const { selectedIntegration } = useSelector(
    (state) => state.meetingIntegrations,
  );

  const integrationName = selectedIntegration?.name?.toLowerCase();
  const iconSrc = getIcon(integrationName);
  const steps = getApiKeySteps[integrationName] || [];
  const config = getSetupConfig(integrationName);
  const fields = config.fields;

  useEffect(() => {
    if (id) dispatch(getIntegration(id));
  }, [id, dispatch]);

  const initialValues = useMemo(
    () => Object.fromEntries(fields.map((f) => [f.name, ""])),
    [fields],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object(
        Object.fromEntries(
          fields.map((f) => [
            f.name,
            f.required === false
              ? Yup.string()
              : Yup.string().required(`${f.label} is required`),
          ]),
        ),
      ),
    [fields],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        if (config.beforeSubmit) await config.beforeSubmit(values);

        const integrationSetting = await dispatch(
          upsertIntegrationSetting(
            config.buildPayload(values, selectedIntegration),
          ),
        ).unwrap();
        await dispatch(getParticipants());

        toast.success("Integration connected successfully");

        if (config.redirectToConfiguration === false) return; 

        navigate(
          user?.isOnboardingCompleted
            ? `/integrations/configuration/${integrationSetting?.uuid}`
            : "/onboarding/note-taker/integrations",
        );
      } catch (err) {
        console.error("Save failed:", err);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            err?.error ||
            "Failed to connect integration",
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  const isSubmitDisabled =
    !formik.isValid ||
    isLoading ||
    fields.some((f) => f.required !== false && !formik.values[f.name]);

  const toggleVisible = (name) =>
    setVisibleFields((prev) => ({ ...prev, [name]: !prev[name] }));

  const renderField = (field, index) => {
    const hasError = formik.touched[field.name] && formik.errors[field.name];
    const isPassword = field.type === "password";
    const isVisible = !!visibleFields[field.name];
    const iconClass = field.iconClass || Icons(field.iconKey) || Icons("edit");

    return (
      <React.Fragment key={field.name}>
        <div
          className={`d-flex align-items-center mb-2 px-3 ${index > 0 ? "mt-4" : ""}`}
          style={{
            height: "52px",
            borderRadius: "10px",
            border: hasError
              ? "1px solid rgba(241, 21, 21, 0.47)"
              : "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <i
            className={`wb ${iconClass} me-2 text-muted`}
            style={{ fontSize: "16px" }}
          />

          <input
            type={isPassword && !isVisible ? "password" : "text"}
            name={field.name}
            className="form-control border-0 bg-transparent shadow-none px-2"
            placeholder={field.placeholder}
            value={formik.values[field.name]}
            autoComplete={field.autoComplete}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{ flex: 1 }}
          />

          {field.suffix && (
            <span
              className="text-muted"
              style={{ whiteSpace: "nowrap", fontSize: "0.9rem" }}
            >
              {field.suffix}
            </span>
          )}

          {isPassword && (
            <i
              className={`wb ${
                isVisible
                  ? Icons("conditionalOpenModal") || Icons("cross")
                  : Icons("system_hide_component")
              } text-muted`}
              style={{ cursor: "pointer", fontSize: "16px" }}
              onClick={() => toggleVisible(field.name)}
            />
          )}
        </div>

        {hasError && (
          <div className="text-danger mb-3" style={{ fontSize: "0.8rem" }}>
            {formik.errors[field.name]}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="container my-6">
      <div className="card border-0 shadow-lg" style={{ marginTop: "60px" }}>
        <div className="card-body p-4 p-md-5">
          <div className="d-flex align-items-center flex-grow-1 w-100 w-md-auto mb-4">
            <div
              className="rounded-circle bg-gradient d-flex justify-content-center align-items-center me-3 flex-shrink-0"
              style={{
                width: "60px",
                height: "60px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 4px 15px rgba(102,126,234,0.4)",
              }}
            >
              <img
                src={iconSrc}
                alt={integrationName}
                style={{ width: "36px", height: "36px" }}
              />
            </div>

            <div>
              <h3 className="mb-1 fw-bold">
                {formatPlatformName(integrationName)} Setup
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                Connect your meeting platform in a few simple steps
              </p>
            </div>
          </div>

          <div
            className="card border border-translucent shadow-sm mb-5"
            style={cardStyle}
          >
            <div className="card-body p-4">
              <h5 className="fw-bold mb-4">Follow these steps</h5>

              <div className="d-flex flex-column gap-2">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="d-flex align-items-center gap-2 px-3 py-2 rounded"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: "24px",
                        height: "24px",
                        fontSize: "12px",
                        fontWeight: "600",
                        background: "rgba(102,126,234,0.2)",
                        color: "#667eea",
                      }}
                    >
                      {index + 1}
                    </div>

                    <div style={{ fontSize: "0.88rem" }}>{step}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className="card border border-translucent shadow-sm"
            style={cardStyle}
          >
            <div className="card-body p-4">
              <h5 className="fw-bold mb-2">{config.title}</h5>

              <p
                className="text-muted mb-4"
                style={{ fontSize: "0.9rem", lineHeight: "1.5" }}
              >
                {config.description}
              </p>

              <form onSubmit={formik.handleSubmit}>
                {fields.map(renderField)}

                <div className="d-flex justify-content-between align-items-center mt-5">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() =>
                      navigate(
                        user?.isOnboardingCompleted
                          ? "/integrations"
                          : "/onboarding/note-taker/integrations",
                      )
                    }
                    disabled={isLoading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary px-4 d-flex align-items-center gap-2"
                    disabled={isSubmitDisabled}
                  >
                    {isLoading && (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                    )}
                    {isLoading ? "Saving..." : "Save & Continue"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SetUp;
